import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button, Card, Badge, Modal, ConfirmDialog } from '../components/UIComponents';
import {
  FileAudio,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertCircle,
  MoreVertical,
  Folder,
  Check,
  Search,
  Download,
  Trash2,
  FileX
} from 'lucide-react';
import Pagination from '../components/Pagination';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranscriptions } from '../hooks/useTranscriptions';
import { useSubscription } from '../hooks/useBilling';
import { deleteOriginalFile } from '../api/files';
import * as transcriptionsApi from '../api/transcriptions';
import EditableFilename from '../components/EditableFilename';
import UpgradeBanner from '../components/UpgradeBanner';
import ExportTranscript, { type ExportOptions } from '../components/ExportTranscript';
import {
  buildExportFilename,
  downloadBlob,
  generateJSON,
  generateMarkdown,
  generateSRT,
  generateTXT,
  generateVTT
} from '../utils/exportUtils';
import { stripKnownTranscriptionTitleExtension } from '../utils/transcriptionTitle';
import { useTransition } from '../hooks/useTransition';
import { useTranslation } from 'react-i18next';

interface ToastProps {
  message: string;
  isVisible: boolean;
}

const Toast: React.FC<ToastProps> = ({ message, isVisible: propIsVisible }) => {
  const { shouldMount, isVisible } = useTransition(propIsVisible, 300);

  if (!shouldMount) return null;

  return (
    <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-50 ${isVisible ? 'toast-in' : 'toast-out'}`}>
      <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-lg shadow-lg">
        <AlertCircle className="h-4 w-4 text-amber-400" />
        <span className="text-sm font-medium">{message}</span>
      </div>
    </div>
  );
};

interface TranscriptionActionMenuProps {
  transcription: transcriptionsApi.TranscriptionResponse;
  onExport: (transcription: transcriptionsApi.TranscriptionResponse) => void;
  onMove: (transcription: transcriptionsApi.TranscriptionResponse) => void;
  onDeleteOriginal: (transcription: transcriptionsApi.TranscriptionResponse) => void;
  onDeleteTranscription: (transcription: transcriptionsApi.TranscriptionResponse) => void;
}

const TranscriptionActionMenu: React.FC<TranscriptionActionMenuProps> = ({
  transcription,
  onExport,
  onMove,
  onDeleteOriginal,
  onDeleteTranscription
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { shouldMount, isVisible } = useTransition(isOpen, 200);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleAction = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    action();
  };

  const canExport = transcription.status === 'completed';
  const canDeleteOriginal = transcription.status === 'completed' && transcription.file_status !== 'transcription_only';

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        aria-label={t('common.moreActions')}
        className="text-slate-400 hover:text-slate-600 dark:text-zinc-500 dark:hover:text-zinc-300 p-2 rounded-md hover:bg-slate-100 dark:hover:bg-zinc-700/60 transition-colors"
        onClick={(e) => { e.stopPropagation(); setIsOpen((prev) => !prev); }}
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {shouldMount && (
        <div className={`absolute right-0 mt-1 w-60 bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-slate-200 dark:border-zinc-700 py-1 z-50 whitespace-nowrap origin-top-right ${isVisible ? 'dropdown-in' : 'dropdown-out'}`}>
          <button
            className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 text-slate-700 dark:text-zinc-200 hover:bg-slate-100 dark:hover:bg-zinc-700 whitespace-nowrap"
            onClick={handleAction(() => onMove(transcription))}
          >
             <Folder className="h-4 w-4" />
             {t('common.move')}
          </button>
          <div className="border-t border-slate-100 dark:border-zinc-700 my-1" />
          <button
            className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 whitespace-nowrap ${
              canExport
                ? 'text-slate-700 dark:text-zinc-200 hover:bg-slate-100 dark:hover:bg-zinc-700'
                : 'text-slate-400 dark:text-zinc-500 cursor-not-allowed'
            }`}
            onClick={canExport ? handleAction(() => onExport(transcription)) : undefined}
            disabled={!canExport}
          >
            <Download className="h-4 w-4" />
            {t('common.export')}
          </button>
          <button
            className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 whitespace-nowrap ${
              canDeleteOriginal
                ? 'text-amber-600 dark:text-amber-400 hover:bg-slate-100 dark:hover:bg-zinc-700'
                : 'text-slate-400 dark:text-zinc-500 cursor-not-allowed'
            }`}
            onClick={canDeleteOriginal ? handleAction(() => onDeleteOriginal(transcription)) : undefined}
            disabled={!canDeleteOriginal}
          >
            <FileX className="h-4 w-4" />
            {t('dashboard.deleteOriginalFile')}
          </button>
          <div className="border-t border-slate-100 dark:border-zinc-700 my-1" />
          <button
            className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-slate-100 dark:hover:bg-zinc-700 flex items-center gap-2 whitespace-nowrap"
            onClick={handleAction(() => onDeleteTranscription(transcription))}
          >
            <Trash2 className="h-4 w-4" />
            {t('dashboard.deleteTranscription')}
          </button>
        </div>
      )}
    </div>
  );
};

interface HistoryProps {
  showFolders?: boolean;
  enableBulkMove?: boolean;
  enableDragMove?: boolean;
}

const History: React.FC<HistoryProps> = ({
  showFolders: _showFolders = false,
  enableBulkMove = true,
  enableDragMove = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { data: subscription, loading: subscriptionLoading } = useSubscription();
  const {
    transcriptions,
    totalTranscriptions,
    totalPages,
    isLoading,
    error,
    fetchTranscriptions,
    refreshTranscriptions,
    deleteTranscription,
    getTranscriptionResult
  } = useTranscriptions();

  const [filter, setFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [renameOverrides, setRenameOverrides] = useState<Record<string, string>>({});
  const [renamingTranscriptionId, setRenamingTranscriptionId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(true);
  const [folderFilter, setFolderFilter] = useState<'all' | 'uncategorized' | string>('all');
  const [folders, setFolders] = useState<transcriptionsApi.TranscriptionFolder[]>([]);
  const [moveTarget, setMoveTarget] = useState<transcriptionsApi.TranscriptionResponse | null>(null);
  const [moveSelectionOpen, setMoveSelectionOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Record<string, true>>({});
  const [bulkMoveDestination, setBulkMoveDestination] = useState<string | null | undefined>(undefined);
  const [bulkMoveSearch, setBulkMoveSearch] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<{
    isOpen: boolean;
    type: 'delete_original' | 'delete_transcription';
    transcription: transcriptionsApi.TranscriptionResponse;
  } | null>(null);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [exportTranscription, setExportTranscription] = useState<transcriptionsApi.TranscriptionResponse | null>(null);
  const [exportSegments, setExportSegments] = useState<transcriptionsApi.TranscriptionSegment[]>([]);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const showToastNotification = useCallback((message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  }, []);

  const notifyFoldersChanged = useCallback(() => {
    window.dispatchEvent(new Event('transcription-folders:changed'));
  }, []);

  const selectedIdList = useMemo(() => Object.keys(selectedIds), [selectedIds]);
  const selectedCount = selectedIdList.length;
  const filteredFoldersForBulkMove = useMemo(() => {
    const query = bulkMoveSearch.trim().toLowerCase();
    if (!query) return folders;
    return folders.filter((f) => f.name.toLowerCase().includes(query));
  }, [bulkMoveSearch, folders]);

  const folderNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const folder of folders) {
      map[folder.id] = folder.name;
    }
    return map;
  }, [folders]);

  const currentFolderName = useMemo(() => {
    if (folderFilter === 'all') return t('history.folder.allTranscriptions', { defaultValue: 'All Transcriptions' });
    if (folderFilter === 'uncategorized') return t('common.uncategorized');
    return folderNameById[folderFilter] ?? t('history.folder.folder');
  }, [folderFilter, folderNameById, t]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const folderParam = params.get('folder');
    if (!folderParam || folderParam === 'all') {
      setFolderFilter('all');
      return;
    }
    if (folderParam === 'uncategorized') {
      setFolderFilter('uncategorized');
      return;
    }
    setFolderFilter(folderParam);
  }, [location.search]);

  const loadFolders = useCallback(async () => {
    try {
      const resp = await transcriptionsApi.listTranscriptionFolders();
      setFolders(resp.folders || []);
    } catch {
      showToastNotification(t('history.toast.failedToLoadFolders'));
    }
  }, [showToastNotification, t]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    const onChanged = () => {
      void loadFolders();
    };
    window.addEventListener('transcription-folders:changed', onChanged);
    return () => {
      window.removeEventListener('transcription-folders:changed', onChanged);
    };
  }, [loadFolders]);

  // Fetch transcriptions when filter or page changes
  const loadTranscriptions = useCallback(() => {
    fetchTranscriptions({
      page: currentPage,
      page_size: pageSize,
      status: filter !== 'all' ? filter as transcriptionsApi.TranscriptionResponse['status'] : undefined,
      folder_id: folderFilter === 'all' ? undefined : folderFilter,
    });
  }, [fetchTranscriptions, currentPage, filter, folderFilter, pageSize]);

  useEffect(() => {
    loadTranscriptions();
  }, [loadTranscriptions]);

  useEffect(() => {
    const onChanged = () => {
      void refreshTranscriptions();
    };
    window.addEventListener('transcriptions:changed', onChanged);
    return () => {
      window.removeEventListener('transcriptions:changed', onChanged);
    };
  }, [refreshTranscriptions]);

  const getStatusIcon = (status: transcriptionsApi.TranscriptionResponse['status']) => {
    switch(status) {
      case 'completed': return <CheckCircle2 className="h-4 w-4" />;
      case 'failed':
      case 'cancelled': return <XCircle className="h-4 w-4" />;
      case 'processing': return <Loader2 className="h-4 w-4 animate-spin" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: transcriptionsApi.TranscriptionResponse['status']) => {
    switch(status) {
      case 'completed': return 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'failed':
      case 'cancelled': return 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400';
      case 'processing': return 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400';
      default: return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
    }
  };

  const getStatusBadge = (status: transcriptionsApi.TranscriptionResponse['status']) => {
    switch(status) {
      case 'completed': return <Badge variant="success">{t('transcription.status.completed')}</Badge>;
      case 'failed': return <Badge variant="warning">{t('transcription.status.failed')}</Badge>;
      case 'cancelled': return <Badge variant="default">{t('transcription.status.cancelled')}</Badge>;
      case 'processing': return <Badge variant="info">{t('transcription.status.processing')}</Badge>;
      case 'queued': return <Badge variant="default">{t('transcription.status.queued')}</Badge>;
      default: return <Badge>{t('transcription.status.queued')}</Badge>;
    }
  };

  const locale = i18n.resolvedLanguage || i18n.language;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return t('time.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('time.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('time.daysAgo', { count: diffDays });
    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleRename = useCallback(
    async (item: transcriptionsApi.TranscriptionResponse, nextName: string, currentName: string) => {
      const transcriptionId = item.id;
      setRenameOverrides((prev) => ({ ...prev, [transcriptionId]: nextName }));
      setRenamingTranscriptionId(transcriptionId);

      try {
        const updatedTranscription = await transcriptionsApi.updateTranscriptionDisplayName(transcriptionId, nextName);
        setRenameOverrides((prev) => ({
          ...prev,
          [transcriptionId]: updatedTranscription.display_name ?? nextName
        }));
      } catch (err) {
        setRenameOverrides((prev) => ({ ...prev, [transcriptionId]: currentName }));
        showToastNotification(t('history.toast.failedToRenameTranscription'));
        throw err;
      } finally {
        setRenamingTranscriptionId((prev) => (prev === transcriptionId ? null : prev));
      }
    },
    [showToastNotification]
  );

  const handleMove = async (folderId: string | null) => {
    if (!moveTarget) return;
    try {
      await transcriptionsApi.moveTranscription(moveTarget.id, folderId);
      setMoveTarget(null);
      await refreshTranscriptions();
      notifyFoldersChanged();
      showToastNotification(t('history.toast.moved'));
    } catch (err) {
      console.error(err);
      showToastNotification(t('history.toast.failedToMove'));
    }
  };

  const handleMoveSelected = async (folderId: string | null) => {
    if (selectedCount === 0) return;
    try {
      await transcriptionsApi.moveTranscriptions(selectedIdList, folderId);
      setMoveSelectionOpen(false);
      setBulkMoveDestination(undefined);
      setBulkMoveSearch('');
      setSelectedIds({});
      await refreshTranscriptions();
      notifyFoldersChanged();
      showToastNotification(t('history.toast.moved'));
    } catch (err) {
      console.error(err);
      showToastNotification(t('history.toast.failedToMove'));
    }
  };

  const closeExportModal = () => {
    setIsExportOpen(false);
    setExportTranscription(null);
    setExportSegments([]);
    setIsExporting(false);
    setIsExportLoading(false);
  };

  const handleExport = async (transcription: transcriptionsApi.TranscriptionResponse) => {
    setExportTranscription(transcription);
    setExportSegments([]);
    setIsExportOpen(true);
    setIsExportLoading(true);

    try {
      const result = await getTranscriptionResult(transcription.id);
      setExportSegments(result.segments || []);
    } catch (err) {
      console.error(err);
      showToastNotification(t('history.toast.failedToLoadTranscript'));
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleDeleteOriginalClick = (transcription: transcriptionsApi.TranscriptionResponse) => {
    setConfirmation({
      isOpen: true,
      type: 'delete_original',
      transcription
    });
  };

  const handleDeleteTranscriptionClick = (transcription: transcriptionsApi.TranscriptionResponse) => {
    setConfirmation({
      isOpen: true,
      type: 'delete_transcription',
      transcription
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmation) return;
    const { type, transcription } = confirmation;

    setActionLoading(transcription.id);
    try {
      if (type === 'delete_original') {
        await deleteOriginalFile(transcription.file_id);
        await refreshTranscriptions();
        showToastNotification(t('history.toast.originalFileDeleted'));
      } else {
        await deleteTranscription(transcription.id);
        notifyFoldersChanged();
        showToastNotification(t('history.toast.transcriptionDeleted'));
      }
      setConfirmation(null);
    } catch (err) {
      console.error(err);
      showToastNotification(t('history.toast.actionFailed'));
      setConfirmation(null);
    } finally {
      setActionLoading(null);
    }
  };

  const handleExportSubmit = (format: string, options: ExportOptions) => {
    if (!exportTranscription) {
      showToastNotification(t('history.toast.noTranscriptionSelected'));
      return;
    }
    if (isExportLoading) {
      showToastNotification(t('history.toast.transcriptStillLoading'));
      return;
    }

    let content = '';
    let mimeType = 'text/plain';
    let extension = format;

    setIsExporting(true);

    try {
      switch (format) {
        case 'txt':
          content = generateTXT(exportSegments, options);
          mimeType = 'text/plain';
          break;
        case 'json':
          content = generateJSON(exportSegments);
          mimeType = 'application/json';
          break;
        case 'md':
          content = generateMarkdown(exportSegments, options);
          mimeType = 'text/markdown';
          break;
        case 'srt':
          content = generateSRT(exportSegments);
          mimeType = 'text/plain';
          break;
        case 'vtt':
          content = generateVTT(exportSegments);
          mimeType = 'text/vtt';
          break;
          case 'docx':
          case 'pdf':
            showToastNotification(t('history.toast.exportComingSoon', { format: format.toUpperCase() }));
            closeExportModal();
            return;
          default:
            showToastNotification(t('history.toast.unsupportedExportFormat'));
            return;
      }

      const exportDisplayName = renameOverrides[exportTranscription.id]
        ?? exportTranscription.display_name
        ?? exportTranscription.file_name
        ?? exportTranscription.file_original_name
        ?? t('transcription.fallbackTitle', { id: exportTranscription.id.slice(-6) });
      const filename = buildExportFilename(
        exportDisplayName,
        extension,
        exportTranscription.display_name_user_set !== true
      );
      downloadBlob(content, filename, mimeType);
      showToastNotification(t('history.toast.exportedAs', { format: format.toUpperCase() }));
      closeExportModal();
    } catch (err) {
      console.error('Export failed:', err);
      showToastNotification(t('history.toast.failedToExportTranscript'));
    } finally {
      setIsExporting(false);
    }
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: true };
    });
  };

  const toggleSelectAllOnPage = () => {
    if (transcriptions.length === 0) return;
    const allSelected = transcriptions.every((t) => !!selectedIds[t.id]);
    if (allSelected) {
      setSelectedIds({});
      return;
    }
    const next: Record<string, true> = {};
    for (const t of transcriptions) {
      next[t.id] = true;
    }
    setSelectedIds(next);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="h-page">{t('nav.transcriptions')}</h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm">
          {t('history.viewingLabel', { defaultValue: 'Viewing:' })} <span className="font-medium text-slate-900 dark:text-zinc-100">{currentFolderName}</span>
        </p>
      </div>

      {/* Upgrade Banner - show only when no subscription and not loading */}
      {!subscriptionLoading && !subscription && showUpgradeBanner && (
        <UpgradeBanner onDismiss={() => setShowUpgradeBanner(false)} />
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
          <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}

      <Card className="relative z-0">
          <div className="p-4 border-b border-slate-100 dark:border-zinc-800">
            <div className="flex items-center justify-between gap-3">
              <div className="flex gap-2 flex-wrap">
               {(['all', 'completed', 'processing', 'failed'] as const).map((f) => (
                 <button
                   key={f}
                   onClick={() => { setFilter(f); setCurrentPage(1); }}
                   className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${
                     filter === f
                       ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'
                       : 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-zinc-700'
                   }`}
                 >
                   {t(`history.filters.${f}`)}
                 </button>
               ))}
              </div>

              {enableBulkMove && selectedCount > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 dark:text-zinc-400">{t('history.selected', { count: selectedCount })}</span>
                  <Button size="sm" onClick={() => setMoveSelectionOpen(true)}>
                    {t('common.move')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setSelectedIds({})}>
                    {t('common.clear')}
                  </Button>
                </div>
              )}
            </div>
          </div>

              <div className="divide-y divide-slate-100 dark:divide-zinc-800">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="p-4 flex items-start gap-4">
                  <div className="h-10 w-10 bg-slate-100 dark:bg-zinc-800 rounded-full animate-pulse"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-100 dark:bg-zinc-800 rounded w-1/3 animate-pulse"></div>
                    <div className="h-3 bg-slate-100 dark:bg-zinc-800 rounded w-1/4 animate-pulse"></div>
                  </div>
                </div>
              ))
            ) : transcriptions.length === 0 ? (
              <div className="p-8 text-center text-slate-500 dark:text-zinc-400 text-sm">
                {t('history.noResults')}
              </div>
            ) : (
              transcriptions.map((item) => {
                const overrideName = renameOverrides[item.id];
                 const rawDisplayName = overrideName
                   ?? item.display_name
                   ?? item.file_name
                   ?? item.file_original_name
                   ?? t('common.untitled');
                 const shouldStripExtension = !overrideName && item.display_name_user_set !== true;
                 const displayName = (shouldStripExtension
                   ? stripKnownTranscriptionTitleExtension(rawDisplayName)
                   : rawDisplayName.trim()) || t('common.untitled');

                return (
                  <div
                    key={item.id}
                    className="p-4 flex items-start gap-4 hover:bg-slate-50 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
                    onClick={() => navigate(`/transcription/${item.id}`)}
                    draggable={enableDragMove}
                    onDragStart={enableDragMove ? (e) => {
                      e.dataTransfer.setData('text/plain', item.id);
                    } : undefined}
                  >
                    {enableBulkMove && (
                    <div className="pt-1">
                      <input
                        type="checkbox"
                        checked={!!selectedIds[item.id]}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleSelected(item.id)}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </div>
                    )}
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${getStatusColor(item.status)}`}>
                      {getStatusIcon(item.status)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-1 gap-3">
                        <div className="min-w-0">
                          <EditableFilename
                            value={displayName}
                            maxLength={255}
                            isLoading={renamingTranscriptionId === item.id}
                            onSave={(nextName) => handleRename(item, nextName, displayName)}
                          />
                          <p className="text-xs text-slate-500 dark:text-zinc-400">
                            {t('transcription.fallbackTitle', { id: item.id.slice(-6) })}
                          </p>
                        </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-slate-400 dark:text-zinc-500 whitespace-nowrap">{formatDate(item.created_at)}</span>
                        <TranscriptionActionMenu
                          transcription={item}
                          onExport={handleExport}
                          onMove={() => setMoveTarget(item)}
                          onDeleteOriginal={handleDeleteOriginalClick}
                          onDeleteTranscription={handleDeleteTranscriptionClick}
                        />
                      </div>
                    </div>

                      <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-zinc-400">
                        {getStatusBadge(item.status)}
                         {folderFilter === 'all' && (
                           <Badge variant="neutral">
                             {item.folder_id ? (folderNameById[item.folder_id] ?? t('common.uncategorized')) : t('common.uncategorized')}
                           </Badge>
                         )}
                        <span className="flex items-center gap-1">
                          <FileAudio className="h-3 w-3" />
                          {formatDuration(item.duration)}
                        </span>
                        <span>{item.language}</span>
                         {item.word_count && <span>{t('transcription.wordCount', { count: item.word_count })}</span>}
                      </div>

                      {item.content_preview && (
                        <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1 line-clamp-2">
                          {item.content_preview}
                        </p>
                      )}

                      {item.error_message && (
                        <p className="text-xs text-red-500 dark:text-red-400 mt-1">{item.error_message}</p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {totalPages > 0 && (
            <div className="p-4 border-t border-slate-100 dark:border-zinc-800">
              {enableBulkMove && (
                <div className="flex items-center justify-between mb-3">
                  <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-zinc-400">
                    <input
                      type="checkbox"
                      checked={transcriptions.length > 0 && transcriptions.every((t) => !!selectedIds[t.id])}
                      onChange={toggleSelectAllOnPage}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    {t('history.selectAllOnPage', { defaultValue: 'Select all on this page' })}
                  </label>
                </div>
              )}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalTranscriptions}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
                isLoading={isLoading}
              />
            </div>
          )}
        </Card>

      <Modal
        isOpen={!!moveTarget}
        onClose={() => setMoveTarget(null)}
        title={t('history.folder.moveToFolder')}
      >
        <div className="space-y-2">
            <button
              className="w-full text-start px-3 py-2 rounded-lg text-sm hover:bg-slate-50 dark:hover:bg-zinc-800"
              onClick={() => handleMove(null)}
            >
              {t('common.uncategorized')}
            </button>
          {folders.map((f) => (
            <button
              key={f.id}
                className="w-full text-start px-3 py-2 rounded-lg text-sm hover:bg-slate-50 dark:hover:bg-zinc-800"
              onClick={() => handleMove(f.id)}
            >
              {f.name}
            </button>
          ))}
        </div>
      </Modal>

      <Modal
        isOpen={moveSelectionOpen}
        onClose={() => { setMoveSelectionOpen(false); setBulkMoveDestination(undefined); setBulkMoveSearch(''); }}
        title={t('history.folder.moveSelectedTitle')}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => { setMoveSelectionOpen(false); setBulkMoveDestination(undefined); setBulkMoveSearch(''); }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => {
                if (bulkMoveDestination === undefined) return;
                void handleMoveSelected(bulkMoveDestination);
              }}
              disabled={bulkMoveDestination === undefined}
            >
              {t('common.move')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="text-sm text-slate-600 dark:text-zinc-400">
            {t('history.folder.moveSelectedTo', { count: selectedCount })}
          </div>

          <div className="relative">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              className="w-full ps-9 pe-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-slate-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder={t('history.folder.searchPlaceholder')}
              value={bulkMoveSearch}
              onChange={(e) => setBulkMoveSearch(e.target.value)}
            />
          </div>

          <div className="border border-slate-200 dark:border-zinc-700 rounded-lg overflow-hidden">
            <button
              className={`w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-slate-50 dark:hover:bg-zinc-800 ${
                bulkMoveDestination === null ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300' : ''
              }`}
              onClick={() => setBulkMoveDestination(null)}
            >
              <span className="flex items-center gap-2">
                <Folder className="h-4 w-4" />
                {t('common.uncategorized')}
              </span>
              {bulkMoveDestination === null && <Check className="h-4 w-4" />}
            </button>

            <div className="border-t border-slate-100 dark:border-zinc-800" />

            <div className="max-h-72 overflow-y-auto">
              {filteredFoldersForBulkMove.map((f) => (
                  <button
                    key={f.id}
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-slate-50 dark:hover:bg-zinc-800 ${
                      bulkMoveDestination === f.id ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300' : ''
                    }`}
                    onClick={() => setBulkMoveDestination(f.id)}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Folder className="h-4 w-4 shrink-0" />
                      <span className="truncate">{f.name}</span>
                    </span>
                    {bulkMoveDestination === f.id && <Check className="h-4 w-4 shrink-0" />}
                  </button>
                ))}
              {folders.length > 0 && filteredFoldersForBulkMove.length === 0 && (
                <div className="px-4 py-6 text-sm text-slate-500 dark:text-zinc-500">
                  {t('history.folder.noMatches')}
                </div>
              )}
              {folders.length === 0 && (
                <div className="px-4 py-6 text-sm text-slate-500 dark:text-zinc-500">
                  {t('history.folder.noneYet')}
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isExportOpen} onClose={closeExportModal} title={t('transcription.export.title')}>
        <ExportTranscript
          onExport={handleExportSubmit}
          onCancel={closeExportModal}
          isExporting={isExporting || isExportLoading}
        />
      </Modal>

      <ConfirmDialog
        isOpen={!!confirmation}
        onClose={() => setConfirmation(null)}
        onConfirm={handleConfirmAction}
        title={confirmation?.type === 'delete_original' ? t('dashboard.confirmDeleteOriginalTitle') : t('dashboard.confirmDeleteTranscriptionTitle')}
        message={confirmation?.type === 'delete_original'
          ? t('dashboard.confirmDeleteOriginalMessage')
          : t('dashboard.confirmDeleteTranscriptionMessage')
        }
        confirmText={t('common.delete')}
        variant="danger"
        isLoading={!!actionLoading}
      />

      <Toast message={toastMessage} isVisible={showToast} />
    </div>
  );
};

export default History;
